// GA4 recommended ecommerce events (BUG-479).
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
//
// The shop calls these helpers; they build the GA4 payload (currency DKK, an
// `items` array) and hand it to track(), which applies the consent gate and
// the redaction and sends it to GTM's dataLayer and to GA4.
//
// Rules:
//   - item_id is the SKU, never the product UUID. Prices come from the
//     catalogue (product/variant price) or, for purchase, from the order the
//     API returned: never recomputed from anything the shopper typed.
//   - No personal data in any payload: no name, e-mail, phone or address.
//   - purchase fires only for a PAID order, once per order (deduped by the
//     human order number in localStorage), with transaction_id = the order
//     number (HH-YYYYMMDD-XXXXXX), never the order UUID.
//   - The older custom names (product_view, cart_add_item, cart_remove_item)
//     still reach GTM's dataLayer for any trigger built on them, but not GA4,
//     which counts these actions under the recommended names instead.

import type { Brand, Category, Order, Product, ProductVariant } from '../shop/types'
import { priceOf, variantLabel } from '../shop/pricing'
import { consent } from '../consent'
import { track, type GaItem } from './track'

export const CURRENCY = 'DKK'

/** The shop listing's name in GA4 reports (item_list_name / item_list_id). */
export const SHOP_LIST = { id: 'shop', name: 'Shop' } as const

/** What the item builders read brand and category names from (the catalogue). */
export interface ShopLookup {
  products: Product[]
  categoryById: Record<string, Category>
  brandById: Record<string, Brand>
}

const round2 = (n: number): number => Math.round(n * 100) / 100

// Lookups are read defensively: analytics must never break the page, also on a partial catalogue.
function brandAndCategory(product: Product | undefined, lookup: Partial<ShopLookup>): Pick<GaItem, 'item_brand' | 'item_category'> {
  if (!product) return {}
  return {
    item_brand: (product.brandId && lookup.brandById?.[product.brandId]?.name) || undefined,
    item_category: lookup.categoryById?.[product.categoryId]?.name,
  }
}

const findProduct = (lookup: Partial<ShopLookup>, id: string): Product | undefined => lookup.products?.find((p) => p.id === id)

/** A GA4 item for a product (and its variant, when it sells in variants). */
export function itemFromProduct(
  product: Product,
  variant: ProductVariant | null,
  lookup: ShopLookup,
  extra: { quantity?: number; index?: number; listName?: string } = {},
): GaItem {
  return {
    item_id: variant?.sku ?? product.sku,
    item_name: product.name,
    ...brandAndCategory(product, lookup),
    item_variant: variant ? variantLabel(variant) : undefined,
    item_list_name: extra.listName,
    index: extra.index,
    price: priceOf(product, variant),
    quantity: extra.quantity ?? 1,
  }
}

/** The fields of a basket line the item builder needs (CartDetailLine has them all). */
export interface CartLineLike {
  productId: string
  sku: string
  name: string
  variantName: string | null
  unitPrice: number
  quantity: number
}

/** A GA4 item for a basket line; `quantity` overrides the line's (a +1 or -1 step). */
export function itemFromCartLine(line: CartLineLike, lookup: ShopLookup, quantity: number = line.quantity): GaItem {
  const product = findProduct(lookup, line.productId)
  return {
    item_id: line.sku,
    item_name: line.name,
    ...brandAndCategory(product, lookup),
    item_variant: line.variantName ?? undefined,
    price: line.unitPrice,
    quantity,
  }
}

/** GA4 items for an order's lines, priced as the server charged them. */
export function itemsFromOrder(order: Pick<Order, 'lines'>, lookup: ShopLookup): GaItem[] {
  return order.lines.map((line) => ({
    item_id: line.sku,
    item_name: line.name,
    ...brandAndCategory(findProduct(lookup, line.productId), lookup),
    item_variant: line.variantName ?? undefined,
    price: line.unitPrice,
    quantity: line.quantity,
  }))
}

/** Sum of price x quantity, to the øre. */
export function itemsValue(items: GaItem[]): number {
  return round2(items.reduce((sum, i) => sum + (i.price ?? 0) * (i.quantity ?? 1), 0))
}

/** The shop listing showed these products (one page of results). */
export function trackViewItemList(items: GaItem[], list: { id: string; name: string } = SHOP_LIST): void {
  if (items.length === 0) return
  track('view_item_list', { item_list_id: list.id, item_list_name: list.name, items })
}

/** The shopper opened a product from the listing. */
export function trackSelectItem(item: GaItem, list: { id: string; name: string } = SHOP_LIST): void {
  track('select_item', { item_list_id: list.id, item_list_name: list.name, items: [item] })
}

/** A product page was shown. */
export function trackViewItem(item: GaItem): void {
  track('view_item', { currency: CURRENCY, value: itemsValue([item]), items: [item] })
  track('product_view', { item_id: item.item_id }, { ga4: false })
}

/** Items went into the basket (`quantity` on the item is the number added). */
export function trackAddToCart(item: GaItem): void {
  track('add_to_cart', { currency: CURRENCY, value: itemsValue([item]), items: [item] })
  track('cart_add_item', { item_id: item.item_id, quantity: item.quantity }, { ga4: false })
}

/** Items left the basket (`quantity` on the item is the number removed). */
export function trackRemoveFromCart(item: GaItem): void {
  track('remove_from_cart', { currency: CURRENCY, value: itemsValue([item]), items: [item] })
  track('cart_remove_item', { item_id: item.item_id, quantity: item.quantity }, { ga4: false })
}

/**
 * A basket line's quantity is about to change to `toQuantity` (0 = removed):
 * add_to_cart or remove_from_cart for the difference. No change, no event.
 */
export function trackCartChange(line: CartLineLike, toQuantity: number, lookup: ShopLookup): void {
  const delta = Math.max(0, toQuantity) - line.quantity
  if (delta > 0) trackAddToCart(itemFromCartLine(line, lookup, delta))
  else if (delta < 0) trackRemoveFromCart(itemFromCartLine(line, lookup, -delta))
}

function cartEvent(event: 'view_cart' | 'begin_checkout' | 'add_shipping_info' | 'add_payment_info') {
  return (items: GaItem[], extra: Record<string, string | undefined> = {}): void => {
    if (items.length === 0) return
    track(event, { currency: CURRENCY, value: itemsValue(items), ...extra, items })
  }
}

/** The cart drawer opened with something in it. */
export const trackViewCart = cartEvent('view_cart')
/** The checkout page opened with a basket. */
export const trackBeginCheckout = cartEvent('begin_checkout')
/** The shopper continued with a delivery method: pass `{ shipping_tier: method name }`. */
export const trackAddShippingInfo = cartEvent('add_shipping_info')
/** The payment step was reached. */
export const trackAddPaymentInfo = cartEvent('add_payment_info')

// --- purchase ---------------------------------------------------------------

export const PURCHASE_STORAGE_KEY = 'helin-ga4-purchases'
const PURCHASE_MEMORY = 50
/** Ids.java in helinhair-webshop: "HH-%s-%06X" (date, six hex digits). */
const ORDER_NUMBER_RE = /^HH-\d{8}-[0-9A-F]{6}$/i

/** Order numbers already reported in this tab, for when storage is unavailable. */
const sentThisSession = new Set<string>()

function readSent(): string[] {
  try {
    const raw = window.localStorage.getItem(PURCHASE_STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

function rememberSent(orderNumber: string): void {
  sentThisSession.add(orderNumber)
  try {
    const next = [...readSent().filter((n) => n !== orderNumber), orderNumber].slice(-PURCHASE_MEMORY)
    window.localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Storage blocked or full: the in-memory set still stops a repeat in this tab.
  }
}

function alreadySent(orderNumber: string): boolean {
  return sentThisSession.has(orderNumber) || readSent().includes(orderNumber)
}

export type PurchaseOrder = Pick<Order, 'orderNumber' | 'paymentStatus' | 'lines' | 'totalAmount' | 'shippingAmount' | 'currency'>

/**
 * Reports a completed order to GA4, once. Only a PAID order with a proper
 * order number counts; the order UUID is never sent. Returns true when the
 * event was sent. The dedupe mark is set only when consent allows the send,
 * so a visitor who accepts cookies later on the same page is still counted.
 */
export function trackPurchase(order: PurchaseOrder, lookup: ShopLookup): boolean {
  if (order.paymentStatus !== 'PAID') return false
  const orderNumber = (order.orderNumber ?? '').trim()
  if (!ORDER_NUMBER_RE.test(orderNumber)) return false
  if (consent.get() !== 'granted' || alreadySent(orderNumber)) return false
  rememberSent(orderNumber)
  track('purchase', {
    transaction_id: orderNumber,
    currency: order.currency || CURRENCY,
    value: round2(order.totalAmount),
    shipping: round2(order.shippingAmount ?? 0),
    items: itemsFromOrder(order, lookup),
  })
  return true
}

export function _resetPurchaseMemoryForTesting(): void {
  sentThisSession.clear()
}
